import { MemberMarkers } from "@prototypes/member-markers/MemberMarkers";
import classNames from "classnames";
import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useAgentsMap } from "../../common/hooks/useAgents";
import { useMembersMap } from "../../common/hooks/useMembers";
import { excludeUndefined } from "../../common/utils/array.util";
import { agentNames } from "../../utils/name.util";
import { firebaseTimestampFromDate } from "../../utils/timestamp.util";
import { AgentAvatarList, MemberAvatar } from "../Common/Avatar";
import { FormattedDateTime } from "../Common/DateTime";
import DeadlineTag from "../Common/DeadlineTag";
import { Flex } from "../Common/Flex";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Label from "../Common/Label";
import { List } from "../Common/List";
import Tag from "../Common/Tag";
import { TicketNumberSmall } from "../Common/TicketNumber";
import Tooltip from "../Common/Tooltip";
import "./TaskCard.css";
import type { TaskDto } from "@albertine/types";
import routes from "../../utils/routes";

type Props = {
    task: TaskDto;
    layout: "horizontal" | "vertical";
};

function TaskCard(props: Props) {
    const { task, layout } = props;

    const agentsMap = useAgentsMap();
    const markedMember = useMembersMap()[task.memberId];
    const isTestMember = markedMember?.isTestMember === true;
    const isVipMember = markedMember?.isVipMember === true;

    const url = useMemo(
        () => routes.chatMessage(task.memberId, task.id, task.requestId || undefined),
        [task],
    );
    const createdBy = useMemo(
        () =>
            agentsMap[task.mentionCreatedBy]
                ? agentsMap[task.mentionCreatedBy].name
                : undefined,
        [agentsMap, task.mentionCreatedBy],
    );
    const createdAt = useMemo(
        () =>
            task.mentionCreatedAt
                ? firebaseTimestampFromDate(new Date(task.mentionCreatedAt))
                : undefined,
        [task.mentionCreatedAt],
    );
    const dueDateAt = useMemo(
        () =>
            task.mentionDueDate
                ? firebaseTimestampFromDate(new Date(task.mentionDueDate))
                : undefined,
        [task.mentionDueDate],
    );
    const resolvedBy = useMemo(
        () =>
            task.mentionResolvedBy && agentsMap[task.mentionResolvedBy]
                ? agentsMap[task.mentionResolvedBy]
                : undefined,
        [agentsMap, task.mentionResolvedBy],
    );
    const resolvedAt = useMemo(
        () =>
            task.mentionResolvedAt
                ? firebaseTimestampFromDate(new Date(task.mentionResolvedAt))
                : undefined,
        [task.mentionResolvedAt],
    );
    const mentionedAgents = useMemo(
        () =>
            task.mentionedAgentIds
                .map((mentioned) => agentsMap[mentioned])
                .filter(excludeUndefined),
        [agentsMap, task.mentionedAgentIds],
    );

    const memberAvatar = (
        <MemberAvatar
            className="task-card__meta-avatar"
            memberId={task.memberId}
            size="big"
        />
    );

    const ticketNumber = task.requestTicketNumber ? (
        <span className="task-card__ticket-number">
            <TicketNumberSmall
                ticketNumber={task.requestTicketNumber}
                className={
                    layout === "vertical" ? "ticket-number--backgroundless" : undefined
                }
            />
        </span>
    ) : null;

    const message = (
        <Label.Medium.Light.N10 className="task-card__message">
            {task.message}
        </Label.Medium.Light.N10>
    );

    const subtitle = useMemo(
        () => (
            <span>
                <Label.Medium.Light.N50>
                    <span className="inline-flex items-center gap-1">
                        {task.memberName}
                        <MemberMarkers
                            isTestMember={isTestMember}
                            isVipMember={isVipMember}
                        />
                    </span>
                    {task?.requestTitle ? `, ${task.requestTitle}` : ""}
                </Label.Medium.Light.N50>

                <InlineTextDelimiter />

                <Label.Medium.Light.N50>
                    <FormattedDateTime.Default timestamp={createdAt} />
                    {createdBy && ` by ${createdBy}`}
                </Label.Medium.Light.N50>
            </span>
        ),
        [
            task.memberName,
            isTestMember,
            isVipMember,
            task.requestTitle,
            createdAt,
            createdBy,
        ],
    );

    const status = (
        <>
            {task.mentionStatus === "open" && (
                <>
                    <Tag.Small.N80>Pending</Tag.Small.N80>
                    {dueDateAt && <DeadlineTag deadlineAt={dueDateAt} />}
                </>
            )}

            {resolvedBy && resolvedAt && (
                <Flex.Column gap="medium">
                    <Tag.Small.P90>Resolved</Tag.Small.P90>

                    {layout === "horizontal" && (
                        <Flex.Row gap="medium">
                            <Label.Small.Light.N10>
                                {resolvedBy.name}
                                <InlineTextDelimiter />
                                <FormattedDateTime.Default timestamp={resolvedAt} />
                            </Label.Small.Light.N10>
                        </Flex.Row>
                    )}
                </Flex.Column>
            )}
        </>
    );

    const agentAvatars = (
        <Tooltip label={agentNames(mentionedAgents)}>
            <AgentAvatarList
                agents={mentionedAgents}
                className="task-list__assignee-avatars"
                showNames
            />
        </Tooltip>
    );

    const horizontalLayout = (
        <>
            <List.ItemColumn>
                <Flex.Row gap="medium" alignItems="start">
                    <Flex.Column
                        className="task-card__meta"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {memberAvatar}
                        {ticketNumber}
                    </Flex.Column>

                    <Flex.Row>
                        <Flex.Column gap="small">
                            {message}
                            {subtitle}
                        </Flex.Column>
                    </Flex.Row>
                </Flex.Row>
            </List.ItemColumn>
            <List.ItemColumn>
                <Flex.Row gap="medium">{status}</Flex.Row>
            </List.ItemColumn>
            <List.ItemColumn>{agentAvatars}</List.ItemColumn>
        </>
    );

    const verticalLayout = (
        <Flex.Row gap="large" alignItems="start">
            <Flex.Column gap="small">
                <Flex.Row gap="small">
                    {ticketNumber}
                    {status}
                </Flex.Row>

                {message}
                {subtitle}
            </Flex.Column>

            <div>{agentAvatars}</div>
        </Flex.Row>
    );

    return (
        <NavLink
            id={task.id}
            className={classNames("list-item", "task-card")}
            to={url}
            activeClassName="selected"
        >
            {layout === "horizontal" ? horizontalLayout : verticalLayout}
        </NavLink>
    );
}

export default TaskCard;
